import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Warning,
  GroupRemove,
  Security,
} from '@mui/icons-material';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface ForceAllReacceptDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ForceAllReacceptDialog: React.FC<ForceAllReacceptDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ 
    users_affected: number; 
    pilots_affected: number; 
    total_affected: number;
    message: string;
  } | null>(null);

  const handleForceAll = async () => {
    if (!confirmChecked) return;

    try {
      setLoading(true);
      setError(null);
      
      // Call endpoint to force all users and pilots to re-accept
      const response = await api.post('/users/force-reaccept-all');
      
      setStats(response.data);
      
      // Wait a moment to show success
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (err: any) {
      console.error('Failed to force re-acceptance for all users:', err);
      setError(err.response?.data?.detail || 'Failed to update users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setConfirmChecked(false);
      setError(null);
      setStats(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'error.main',
        color: 'white'
      }}>
        <GroupRemove />
        {t('users.forceAllReaccept.title')}
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="error" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ⚠️ {t('users.forceAllReaccept.criticalAction')}
          </Typography>
          <Typography variant="body2">
            {t('users.forceAllReaccept.invalidateDescription')}
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • <strong>{t('users.forceAllReaccept.affectedUsers')}</strong><br/>
            • <strong>{t('users.forceAllReaccept.affectedPilots')}</strong><br/>
            {t('users.forceAllReaccept.forcedReaccept')}
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t('users.forceAllReaccept.willDo')}:
          </Typography>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <Typography variant="body2">
                {t('users.forceAllReaccept.resetUsers')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('users.forceAllReaccept.resetPilots')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('users.forceAllReaccept.forceReacceptAll')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('users.forceAllReaccept.lockout')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('users.forceAllReaccept.auditLog')}
              </Typography>
            </li>
          </ul>
        </Box>

        <Alert severity="info" icon={<Security />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{t('users.forceAllReaccept.useCaseLabel')}:</strong> {t('users.forceAllReaccept.useCaseDescription')}
          </Typography>
        </Alert>

        <FormControlLabel
          control={
            <Checkbox
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              color="error"
              disabled={loading}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
              {t('users.forceAllReaccept.confirmText')}
            </Typography>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {stats && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ✅ {t('users.forceAllReaccept.successTitle')}
            </Typography>
            <Typography variant="body2">
              • {t('users.forceAllReaccept.usersAffected')}: <strong>{stats.users_affected}</strong><br/>
              • {t('users.forceAllReaccept.pilotsAffected')}: <strong>{stats.pilots_affected}</strong><br/>
              • {t('users.forceAllReaccept.totalAffected')}: <strong>{stats.total_affected}</strong><br/>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {stats.message}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleForceAll}
          color="error"
          variant="contained"
          disabled={loading || !confirmChecked}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Warning />}
        >
          {loading ? t('users.forceAllReaccept.updating') : t('users.forceAllReaccept.buttonText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForceAllReacceptDialog;